// filepath: apps/web/src/features/admin/queries.ts
//
// FR-UI-ADM-1 — TanStack Query hooks for the entity-type CRUD
// permission matrix editor.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1 / FR-UI-CORE-1 pattern). Switching tenants does
// NOT leak the prior tenant's matrix — the cache key changes,
// the query refetches.
//
// Mutations carry `idempotency_key: crypto.randomUUID()` (INV-3 /
// FR-UI-PROM-2 pattern) so retries are idempotent.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  EntityDefinitionSummary,
  MatrixState,
  PermissionsResponse,
  PromotionReviewSummary,
  RoleResponse,
  StagingOnlyError,
  VersionConflictError,
} from "./types";

/** Cache keys for the matrix editor. tenantSlug is the 2nd segment (INV-1). */
export const adminKeys = {
  matrix: (tenantSlug: string) =>
    ["admin", tenantSlug, "permissions-matrix", "staging"] as const,
  pendingReviews: (tenantSlug: string) =>
    ["admin", tenantSlug, "pending-reviews", "permission"] as const,
  roleList: (tenantSlug: string) => ["admin", tenantSlug, "roles"] as const,
  rolePermissions: (tenantSlug: string, roleName: string) =>
    ["admin", tenantSlug, "role-permissions", roleName] as const,
  entityDefinitions: (tenantSlug: string) =>
    ["admin", tenantSlug, "entity-definitions"] as const,
  // FR-UI-ADM-4 — admin viewer cache key. tenantSlug is the 2nd
  // segment (INV-1).
  fieldVisibility: (tenantSlug: string, roleName: string, entityType: string) =>
    ["admin", tenantSlug, "field-visibility", roleName, entityType] as const,
};

/**
 * useRoleList — GET /api/roles → RoleResponse[].
 * The list is already ordered is_system DESC, name ASC on the server.
 */
export function useRoleList() {
  const { tenantSlug } = useTenant();
  return useQuery<RoleResponse[], ApiError>({
    queryKey: adminKeys.roleList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch(`/api/roles`);
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as RoleResponse[];
    },
  });
}

/**
 * useRolePermissions — GET /api/roles/{roleName}/permissions → PermissionsResponse.
 * Reads the version field so the consumer can echo it back as If-Match.
 */
export function useRolePermissions(roleName: string) {
  const { tenantSlug } = useTenant();
  return useQuery<PermissionsResponse, ApiError>({
    queryKey: adminKeys.rolePermissions(tenantSlug, roleName),
    queryFn: async () => {
      const r = await apiFetch(
        `/api/roles/${encodeURIComponent(roleName)}/permissions`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as PermissionsResponse;
    },
    enabled: Boolean(roleName),
  });
}

/**
 * useEntityTypeList — GET /api/entity-definitions → EntityDefinitionSummary[].
 * Returns the entity types that exist in the resolved tenant schema.
 * The matrix editor uses EntityDefinitionSummary.entity_type as the
 * column headers.
 */
export function useEntityTypeList() {
  const { tenantSlug } = useTenant();
  return useQuery<EntityDefinitionSummary[], ApiError>({
    queryKey: adminKeys.entityDefinitions(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch(`/api/entity-definitions`);
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as EntityDefinitionSummary[];
    },
  });
}

/**
 * usePermissionsMatrix — combined fetch: roles + per-role permissions +
 * entity definitions → MatrixState.
 *
 * The three fetches run in parallel inside the queryFn so the
 * MatrixState is available atomically once the query resolves. On
 * any one failure the typed ApiError propagates so the page can map
 * it to one of the 5 error states.
 */
export function usePermissionsMatrix() {
  const { tenantSlug } = useTenant();
  return useQuery<MatrixState, ApiError>({
    queryKey: adminKeys.matrix(tenantSlug),
    queryFn: async (): Promise<MatrixState> => {
      const [roles, entityDefs] = await Promise.all([
        apiFetch(`/api/roles`).then(async (r) => {
          if (!r.ok) {
            throw new ApiError(r.statusText, r.status, `http_${r.status}`);
          }
          return (await r.json()) as RoleResponse[];
        }),
        apiFetch(`/api/entity-definitions`).then(async (r) => {
          if (!r.ok) {
            throw new ApiError(r.statusText, r.status, `http_${r.status}`);
          }
          return (await r.json()) as EntityDefinitionSummary[];
        }),
      ]);

      // Fetch per-role permissions in parallel. Each failure throws,
      // aborting the entire matrix — the page maps the error to a
      // dedicated state.
      const permsEntries = await Promise.all(
        roles.map(async (role) => {
          const r = await apiFetch(
            `/api/roles/${encodeURIComponent(role.name)}/permissions`,
          );
          if (!r.ok) {
            throw new ApiError(r.statusText, r.status, `http_${r.status}`);
          }
          const data = (await r.json()) as PermissionsResponse;
          return [role.name, data] as const;
        }),
      );
      const permissions: Record<string, PermissionsResponse> = {};
      for (const [name, data] of permsEntries) {
        permissions[name] = data;
      }

      const entityTypes = entityDefs.map((d) => d.entity_type);
      return { roles, permissions, entityTypes };
    },
  });
}

interface SaveVariables {
  roleName: string;
  /** Required: the version echoed by the GET the editor read from. */
  version: number;
  permissions: PermissionsResponse;
}

/**
 * useSaveRoleMatrix — PUT /api/roles/{roleName}/permissions.
 *
 * Sends `If-Match: <version>` header on every call (INV-7).
 * On 200 → invalidate the matrix + pending-reviews caches so the
 *           banner + grid update.
 * On 409 → throw VersionConflictError so the page can open the
 *           MatrixConflictDialog (FR-UI-CORE-6 refetch / merge /
 *           discard UX).
 * On 403 with code ENV_PRODUCTION → throw StagingOnlyError.
 * On other failures → throw ApiError.
 *
 * Mutation carries `idempotency_key: crypto.randomUUID()` so retries
 * are idempotent (INV-3).
 */
export function useSaveRoleMatrix() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<PermissionsResponse, Error, SaveVariables>({
    mutationFn: async ({ roleName, version, permissions }) => {
      const res = await apiFetch(
        `/api/roles/${encodeURIComponent(roleName)}/permissions`,
        {
          method: "PUT",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": String(version),
            },
            body: JSON.stringify({
              permissions: permissions.permissions,
              idempotency_key: crypto.randomUUID(),
            }),
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_matrix?: PermissionsResponse;
        };
        if (body.code === "ROLE_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            kind: "version_conflict",
            currentVersion: body.current_version ?? 0,
            serverMatrix: body.server_matrix ?? {
              role: roleName,
              permissions: [],
              version: 0,
            },
            message: body.error ?? "version_conflict",
          };
          throw err;
        }
      }
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message: body.error ?? "matrix editor only writes to staging",
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as PermissionsResponse;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.matrix(tenantSlug),
      });
      queryClient.invalidateQueries({
        queryKey: adminKeys.rolePermissions(tenantSlug, variables.roleName),
      });
      queryClient.invalidateQueries({
        queryKey: adminKeys.pendingReviews(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * usePromotionPendingCount — GET /api/admin/promotion-reviews?def_type=permission&status=pending_review
 * → array length. Returns 0 when none.
 */
export function usePromotionPendingCount() {
  const { tenantSlug } = useTenant();
  return useQuery<number, ApiError>({
    queryKey: adminKeys.pendingReviews(tenantSlug),
    queryFn: async () => {
      const url = `/api/admin/promotion-reviews?def_type=permission&status=pending_review`;
      const r = await apiFetch(url);
      if (!r.ok) {
        if (r.status === 404) {
          // No reviews exist yet — empty list.
          return 0;
        }
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      const data = (await r.json()) as PromotionReviewSummary[];
      return data.length;
    },
  });
}

/**
 * useCreateRole — POST /api/roles. Creates a new tenant-defined role.
 */
export function useCreateRole() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    RoleResponse,
    Error,
    { name: string; description?: string }
  >({
    mutationFn: async ({ name, description }) => {
      const res = await apiFetch(`/api/roles`, {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            description: description ?? "",
            idempotency_key: crypto.randomUUID(),
          }),
        },
        idempotencyKey: true,
      });
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as RoleResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.matrix(tenantSlug) });
      queryClient.invalidateQueries({
        queryKey: adminKeys.roleList(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useDeleteRole — DELETE /api/roles/{roleName}. Refuses on system roles
 * (the server returns HTTP 403 forbidden).
 */
export function useDeleteRole() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { roleName: string }>({
    mutationFn: async ({ roleName }) => {
      const res = await apiFetch(`/api/roles/${encodeURIComponent(roleName)}`, {
        method: "DELETE",
        idempotencyKey: true,
      });
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.matrix(tenantSlug) });
      queryClient.invalidateQueries({
        queryKey: adminKeys.roleList(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}
